import FormControlledInput from '../../../../components/inputs/FormInputs/FormControlledInput';
import FormControlledCalendar from '../../../../components/inputs/FormInputs/FormControlledCalendar';
import FormControlledInputArea from '../../../../components/inputs/FormInputs/FormControlledInputArea';
import FormControlledDropdown from '../../../../components/inputs/FormInputs/FormControlledDropdown';
import { useQuery } from '@tanstack/react-query';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { fetchPREmployeeList } from '../../../../services/accounting/PayrollService';
import { Employee } from '../../../../Interfaces/Accounting/Payroll.interface';
import { formatDate } from '@/src/utils/formatUtils';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  ClaimForm,
  ClaimPartyInvolved,
  ClaimStatus,
} from '../../../../Interfaces/Insurance/Claim';
import ContactClaimForm from './ContactClaimForm';
import WrapperButton from '../../../../components/buttons/WrapperButton';
import ClaimLogForm from './ClaimLogForm';
import React, { useState } from 'react';
import { Status } from '@/src/app/Interfaces/system/Generic.interfaces';
import SingleFileInput from '@/src/app/components/inputs/StyledInputs/SingleFileInput';
import { FILE_SIZE_LIMIT } from '@/src/utils/fileConsts';
import { transformBToMB } from '@/src/utils/fileUtil';
import { fetchVendors } from '@/src/app/services/accounting/vendorsService';
import { getVendorKeyPerCompany } from '../../../accounting/accountsPayables/apUtil';
import { getSource } from '@/src/utils/company';
import { APVendor } from '@/src/app/Interfaces/Accounting/AccountsPayables.interfaces';
import { CompanySource } from '@/src/app/Interfaces/admin/User.interfaces';
import { fetchPolicies } from '@/src/app/services/Insurance/Policy';

type ClaimsFormProps = {
  showUpdateFields: boolean;
  prevAttachment?: string;
};

const ClaimsForm = ({ showUpdateFields, prevAttachment }: ClaimsFormProps) => {
  const { selectedCompany } = useCompanyContext();
  const vendorKey = getVendorKeyPerCompany(getSource(selectedCompany));
  const [fileLimit, setFileLimit] = useState<number>(FILE_SIZE_LIMIT);
  const { control, setValue } = useFormContext<ClaimForm>();
  const removed = useWatch<ClaimForm>({ name: 'removeAttachment' });
  const relation = useWatch<ClaimForm>({ name: 'relation' }) as string;
  const { fields, append, remove } = useFieldArray<ClaimForm>({
    control,
    name: 'contacts',
  });
  const logProcess = useFieldArray<ClaimForm>({
    control,
    name: 'logs',
  });

  const empRequest = useQuery({
    queryKey: ['getClaimEmployees', selectedCompany],
    queryFn: ({ signal }) => {
      return fetchPREmployeeList({ companyID: selectedCompany!.id }, signal);
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && relation === 'Employee'),
  });

  const vendors = useQuery({
    queryKey: ['getSubcontractVendors', selectedCompany],
    queryFn: ({ signal }) => {
      return fetchVendors(
        {
          company: selectedCompany!,
          active: true,
          types: ['Subcontractor'],
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && relation === 'Subcontractor'),
  });

  const polRequest = useQuery({
    queryKey: ['getClaimPolicies', selectedCompany],
    queryFn: ({ signal }) => {
      return fetchPolicies(selectedCompany!.id, Status.both, signal);
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  return (
    <div className="grid mx-3 justify-content-center">
      <div className="col-12 sm:col-6">
        <label htmlFor="policyID" className="text-standard block mb-2">
          Policy
        </label>
        <FormControlledDropdown
          formID="policyID"
          options={
            polRequest.data?.map((policy) => ({
              id: policy.id,
              label: `${policy.policy} ${formatDate(
                policy.termStart
              )} - ${formatDate(policy.termEnd)}`,
            })) ?? []
          }
          labelField="label"
          valueField="id"
          placeholder={
            polRequest.isError ? 'Failed to load policies!' : 'Select Policy'
          }
          isDisabled={polRequest.isError}
          isLoading={polRequest.isFetching}
          clearable={true}
          filter={true}
          resetFilterOnHide={true}
          rules={{
            required: {
              value: true,
              message: 'Policy is a required field',
            },
          }}
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="type" className="text-standard block mb-2">
          Claim Type
        </label>
        <FormControlledInput defaultValue="" formID="type" autocomplete="off" />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="relation" className="text-standard block mb-2">
          Party Involved
        </label>
        <FormControlledDropdown
          formID="relation"
          options={
            CompanySource.Yardi === selectedCompany!.source
              ? [ClaimPartyInvolved.Other]
              : Object.values(ClaimPartyInvolved)
          }
          placeholder={'Select option'}
          clearable={false}
          additionalChange={() => {
            setValue('employeeID', undefined);
            setValue('subcontractorID', undefined);
            setValue('other', undefined);
          }}
        />
      </div>
      <div className="col-12 sm:col-6">
        {relation === 'Employee' && (
          <React.Fragment>
            <label htmlFor="employeeID" className="text-standard block mb-2">
              Employee
            </label>
            <FormControlledDropdown
              formID="employeeID"
              options={
                empRequest.data?.map((emp: Employee) => {
                  return {
                    ...emp,
                    EmpID: `${emp.EmpID}`,
                    label: `${emp.EmpID} ${emp.FirstName} ${emp.LastName}`,
                  };
                }) ?? []
              }
              labelField="label"
              valueField="EmpID"
              placeholder={
                empRequest.isError
                  ? 'Failed to load employees!'
                  : 'Select Employee'
              }
              isDisabled={empRequest.isError}
              isLoading={empRequest.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
            />
          </React.Fragment>
        )}
        {relation === 'Subcontractor' && (
          <React.Fragment>
            <label
              htmlFor="subcontractorID"
              className="text-standard block mb-2"
            >
              Subcontractor
            </label>
            <FormControlledDropdown
              formID="subcontractorID"
              options={
                vendors.data?.map((vendor: APVendor) => {
                  return {
                    ...vendor,
                    code: `${vendor.code}`,
                  };
                }) ?? []
              }
              labelField="name"
              valueField={vendorKey}
              placeholder={
                vendors.isError
                  ? 'Failed to load subcontractors!'
                  : 'Select subcontractor'
              }
              isDisabled={vendors.isError}
              isLoading={vendors.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
            />
          </React.Fragment>
        )}
        {relation === 'Other' && (
          <React.Fragment>
            <label htmlFor="other" className="text-standard block mb-2">
              Other
            </label>
            <FormControlledInput
              defaultValue=""
              formID="other"
              autocomplete="off"
            />
          </React.Fragment>
        )}
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="number" className="text-standard block mb-2">
          Claim #
        </label>
        <FormControlledInput
          defaultValue=""
          formID="number"
          autocomplete="off"
          rules={{
            required: {
              value: true,
              message: 'Claim is a required field',
            },
          }}
        />
      </div>
      <div className="col-12 sm:col-6" />
      <div className="col-12 pb-0 flex justify-content-between align-content-center">
        <label className="block font-bold text-20px">
          Insurance Contact Info
        </label>
        <WrapperButton onClick={() => append({ name: '' })} type="button">
          <i className="pi pi-plus" />
        </WrapperButton>
      </div>
      {fields?.map((field, index) => {
        return (
          <ContactClaimForm key={field.id} fieldIndex={index} remove={remove} />
        );
      })}
      <div className="col-12 pb-0">
        <label className="block font-bold text-20px">Accident Info</label>
      </div>
      <div className="col-12 sm:col-6 mr-auto">
        <label htmlFor="accidentDate" className="text-standard block mb-2">
          Date
        </label>
        <FormControlledCalendar
          formID="accidentDate"
          readOnlyInput
          showButtonBar
          className="w-full"
          rules={{
            required: {
              value: true,
              message: 'Date of Accident is a required field',
            },
          }}
        />
      </div>
      {showUpdateFields && (
        <div className="col-12 sm:col-6">
          <label htmlFor="status" className="text-standard block mb-2">
            Status
          </label>
          <FormControlledDropdown
            formID="status"
            options={[
              { label: 'Active', value: ClaimStatus.Active },
              { label: 'Closed', value: ClaimStatus.Closed },
              { label: 'On Notice', value: ClaimStatus.OnNotice },
            ]}
            labelField="label"
            valueField="value"
            placeholder={'Select Claim Status'}
          />
        </div>
      )}
      <div className="col-12">
        <label
          htmlFor="accidentDescription"
          className="text-standard block mb-2"
        >
          Description
        </label>
        <FormControlledInputArea
          defaultValue=""
          formID="accidentDescription"
          rows={5}
          cols={60}
          rules={{
            required: {
              value: true,
              message: 'Date of Accident is a required field',
            },
          }}
        />
      </div>
      <div className="col-12">
        <label htmlFor="attachment" className="text-standard block mb-2">
          Attachment
        </label>
        <div>
          {prevAttachment && !removed && (
            <div>
              <div className="flex">
                Current attachment:
                <div className="flex flex-column gap-2">
                  <span className="flex">
                    <span className="ml-1 mr-2">{prevAttachment}</span>
                    <i
                      onClick={() => {
                        setValue('removeAttachment', true);
                      }}
                      className={`pi pi-ban errorText cursor-pointer my-auto`}
                    />
                  </span>
                </div>
              </div>
              <div className="mt-2 mb-1">
                To add a new attachment select it below:
              </div>
            </div>
          )}
          <Controller
            name="attachment"
            aria-label="file-input"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <div>
                  <SingleFileInput
                    value={value}
                    onChange={(event) => {
                      onChange(event);
                      if (event) {
                        setFileLimit((current) => current - event.size);
                      } else {
                        setFileLimit((current) => current + (value?.size ?? 0));
                      }
                    }}
                    acceptedFormats={['.pdf']}
                    totalLimit={fileLimit + (value?.size ?? 0)}
                    totalLimitLabel={`${transformBToMB(
                      fileLimit + (value?.size ?? 0)
                    )} MB`}
                    emptyMessage={`Upload attachment file. Allowed extensions: ${[
                      '.pdf',
                    ].join(', ')}.`}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>
      {showUpdateFields && (
        <React.Fragment>
          <div className="col-12 pb-0 flex justify-content-between align-content-center">
            <label className="block font-bold text-20px">Logs</label>
            <WrapperButton
              onClick={() =>
                logProcess.append({ date: new Date(), description: '' })
              }
              type="button"
            >
              <i className="pi pi-plus" />
            </WrapperButton>
          </div>
          {logProcess.fields?.map((field, index) => {
            return (
              <ClaimLogForm
                key={field.id}
                fieldIndex={index}
                remove={logProcess.remove}
                fileLimit={fileLimit}
                changeLimit={(amount) => {
                  setFileLimit((current) => current + amount);
                }}
              />
            );
          })}
        </React.Fragment>
      )}
    </div>
  );
};

export default ClaimsForm;
